import { GameState, State } from '../../types';
import { ZodiacFEN } from '../../Fen/ZodiacFEN';
import { AIMoveResult } from '../Core/IAIPlayer';
import { evaluate, ASTRA_VERSION, EvaluationTerms } from './AstraEvaluator';
import { describeMove } from './AstraRules';
import { BLIND_VERSION } from '../AstraBlindAI';

export interface AstraMoveRecord {
  ply:number;turn:number;side:'light'|'dark';model:string;move:string;beforeFen:string;afterFen:string;
  before:EvaluationTerms;after:EvaluationTerms;thinkingMs:number;budgetMs:number;
  searchScore?:number;diagnostics?:any;reasoning?:string;executionSucceeded:boolean;predictionMatched?:boolean;
}
export interface AstraGameReport {
  schemaVersion:1;version:string;id:string;startedAt:string;finishedAt?:string;
  models:{light:string;dark:string};budgets:{light:number;dark:number};initialFen?:string;finalFen?:string;
  outcome?:string;winCondition?:string;summary:string[];reviewQuestions:string[];expertNotes:string;
  moves:AstraMoveRecord[];fenHistory:string[];limitations:string[];
}
const STORAGE_KEY='zodiac_astra_reports_v1';

export class AstraReportRecorder {
  private report:AstraGameReport|null=null;
  start(light:string,dark:string,lightMs:number,darkMs:number):void {
    const blind=light==='zstr-blind'||dark==='zstr-blind';
    this.report=light==='zstr'||dark==='zstr'||blind?{
      schemaVersion:1,version:blind?BLIND_VERSION:ASTRA_VERSION,id:`astra-${Date.now()}-${crypto.randomUUID().slice(0,8)}`,startedAt:new Date().toISOString(),
      models:{light,dark},budgets:{light:lightMs,dark:darkMs},summary:[],reviewQuestions:[],expertNotes:'',moves:[],fenHistory:[],
      limitations:['Static component scores describe positions; they do not prove why a game was won.',
        'Only AI decisions have search diagnostics. Full FEN history includes human moves.',
        'MM and ZSTR search scores have different scales; position changes use only the ZSTR evaluator.',
        blind?'Blind search masks enemy king identity. Reports retain actual kings for postgame review only; they are not search inputs.':'Search sees the same full game state as MM, including king identities.',
        'Challenge TestAI mode makes dangerous Monkey leaps succeed; Monkey-versus-Bear combat still rolls dice.'],
    }:null;
  }
  record(before:GameState,after:GameState,result:AIMoveResult,model:string,budgetMs:number,success:boolean):void {
    if(!this.report) return;
    const beforeFen=ZodiacFEN.encode(before);
    const afterFen=ZodiacFEN.encode(after);
    const predicted:string[]|undefined=result.evaluationBreakdown?.astra?.predictedAfterFens;
    const clean=(fen:string)=>fen.split('@')[0].split('#')[0].split('~')[0];
    this.report.initialFen ||= beforeFen;
    this.report.moves.push({ply:this.report.moves.length+1,turn:before.turnCount,side:before.isLightTurn?'light':'dark',
      model,move:describeMove(before,{fromPosition:result.fromPosition,move:result.move}),beforeFen,afterFen,
      before:evaluate(before),after:evaluate(after),thinkingMs:result.thinkingTime,budgetMs,searchScore:result.evaluation,
      diagnostics:result.evaluationBreakdown?.astra,reasoning:result.reasoning,executionSucceeded:success,
      predictionMatched:predicted?.length?predicted.some(fen=>clean(fen)===clean(afterFen)):undefined});
    this.report.fenHistory=[...after.fenHistory];
  }
  finish(state:GameState):AstraGameReport|null {
    if(!this.report) return null;
    const report=this.report;
    report.finishedAt=new Date().toISOString();report.finalFen=ZodiacFEN.encode(state);report.fenHistory=[...state.fenHistory];
    const draw=state.state===State.Draw || (state as any).isDraw || state.lightWon===undefined;
    report.outcome=state.state===State.Terminated?'terminated':draw?'draw':state.lightWon?'light wins':'dark wins';
    report.winCondition=(state as any).winCondition || (state as any).drawReason || 'unspecified';
    report.summary=[`Observed result: ${report.outcome}; ending: ${report.winCondition}.`];
    const astraMoves=report.moves.filter(m=>m.model==='zstr'||m.model==='zstr-blind');
    if(astraMoves.length) {
      const depths=astraMoves.map(m=>m.diagnostics?.depth||0);
      report.summary.push(`ZSTR completed depth ${Math.min(...depths)}-${Math.max(...depths)} across ${astraMoves.length} ${astraMoves.length===1?'decision':'decisions'}.`);
    }
    const swings=report.moves.map(m=>({m,delta:m.after.total-m.before.total})).sort((a,b)=>Math.abs(b.delta)-Math.abs(a.delta));
    for(const {m,delta} of swings.slice(0,3)) {
      const changes=(['material','race','coordination','restrictions'] as const).map(k=>`${k} ${Math.round(m.after[k]-m.before[k])}`).join(', ');
      report.summary.push(`Review move ${m.ply}: ${m.side} ${m.move}; static change ${Math.round(delta)} (Light-positive; ${changes}). This is a review candidate, not a proven blunder.`);
    }
    const failed=report.moves.filter(m=>!m.executionSucceeded);
    if(failed.length) report.summary.push(`${failed.length} move execution failures recorded; inspect these before judging strength.`);
    const mismatches=astraMoves.filter(m=>m.predictionMatched===false);
    if(mismatches.length) report.summary.push(`Simulation review needed at moves ${mismatches.map(m=>m.ply).join(', ')}: the actual position differed from every predicted dice outcome.`);
    // Evaluate the position MM left before each later ZSTR turn, using the same evaluator throughout.
    const reversals=astraMoves.slice(1).map((m,i)=>({m,delta:(m.before.total-astraMoves[i].before.total)*(m.side==='light'?1:-1)}))
      .sort((a,b)=>a.delta-b.delta);
    report.reviewQuestions=[
      'Which opening setup gave one side a lasting advantage, and which earlier move should ZSTR have changed?',
      reversals[0]?`At move ${reversals[0].m.ply}, ZSTR's static position changed by ${Math.round(reversals[0].delta)} since its previous decision. Was this a bad plan, a necessary sacrifice, or a misleading evaluation?`:'Did ZSTR miss a useful formation or king route?',
      'Was the decisive problem positioning, a tactical horizon, a rule misunderstanding, or dice? What line would you play instead?',
    ];
    return JSON.parse(JSON.stringify(report));
  }
}

export function saveAstraReport(report:AstraGameReport):boolean {
  try {
    const previous=JSON.parse(localStorage.getItem(STORAGE_KEY)||'[]') as AstraGameReport[];
    localStorage.setItem(STORAGE_KEY,JSON.stringify([...previous.filter(r=>r.id!==report.id).slice(-9),report]));
    return true;
  } catch {return false;}
}
export function loadLatestAstraReport():AstraGameReport|null {
  try {const reports=JSON.parse(localStorage.getItem(STORAGE_KEY)||'[]');return reports[reports.length-1]||null;}
  catch {return null;}
}
export function downloadAstraReport(report:AstraGameReport):void {
  const url=URL.createObjectURL(new Blob([JSON.stringify(report,null,2)],{type:'application/json'}));
  const link=document.createElement('a');link.href=url;link.download=`${report.id}-${report.version}.json`;link.click();
  setTimeout(()=>URL.revokeObjectURL(url),1000);
}
export function downloadSavedAstraReports():void {
  const games=JSON.parse(localStorage.getItem(STORAGE_KEY)||'[]');
  const url=URL.createObjectURL(new Blob([JSON.stringify({schemaVersion:1,games},null,2)],{type:'application/json'}));
  const link=document.createElement('a');link.href=url;link.download=`astra-round-${Date.now()}.json`;link.click();
  setTimeout(()=>URL.revokeObjectURL(url),1000);
}
